import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { Skeleton } from "@/components/ui/skeleton";

// Critical pages - loaded eagerly for fast initial load
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// SEO Migration - Redirect handler for old URLs
import RedirectHandler from "./components/RedirectHandler";
import ScrollToTop from "./components/ScrollToTop";

// Page loading skeleton
const PageLoader = () => (
  <div className="min-h-screen bg-background">
    <div className="h-16 bg-primary" />
    <div className="h-80 bg-muted animate-pulse" />
    <div className="container mx-auto px-4 py-12 space-y-6">
      <Skeleton className="h-8 w-2/3 mx-auto" />
      <Skeleton className="h-4 w-1/2 mx-auto" />
      <div className="grid md:grid-cols-3 gap-6 mt-8">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

// Lazy-loaded pages for code splitting
const About = lazy(() => import("./pages/About"));
const AuthorProfile = lazy(() => import("./pages/AuthorProfile"));
const Services = lazy(() => import("./pages/Services"));
const Solutions = lazy(() => import("./pages/Solutions"));
const SolutionDetail = lazy(() => import("./pages/solutions/SolutionDetail"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const CaseStudy = lazy(() => import("./pages/CaseStudy"));
const AgencyCaseStudies = lazy(() => import("./pages/AgencyCaseStudies"));
const AgencyCaseStudyDetail = lazy(() => import("./pages/AgencyCaseStudyDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const Offer = lazy(() => import("./pages/Offer"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogCategories = lazy(() => import("./pages/BlogCategories"));
const BlogTag = lazy(() => import("./pages/BlogTag"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Hosting = lazy(() => import("./pages/Hosting"));

// Court Services showcase
const CourtHome = lazy(() => import("./pages/showcase/court-services/CourtHome"));
const CourtServicesPage = lazy(() => import("./pages/showcase/court-services/CourtServicesPage"));
const CourtJurisdictions = lazy(() => import("./pages/showcase/court-services/CourtJurisdictions"));
const CourtFAQ = lazy(() => import("./pages/showcase/court-services/CourtFAQ"));
const CourtContact = lazy(() => import("./pages/showcase/court-services/CourtContact"));
const CourtAbout = lazy(() => import("./pages/showcase/court-services/CourtAbout"));

// Lazy-loaded public pages
const PublicQuote = lazy(() => import("./pages/PublicQuote"));
const ClientIntake = lazy(() => import("./pages/ClientIntake"));
const Architect = lazy(() => import("./pages/Architect"));
const ArchitectV2 = lazy(() => import("./pages/ArchitectV2"));

// Lazy-loaded funnel pages
const SEOAuditFunnel = lazy(() => import("./pages/funnels/SEOAuditFunnel"));
const WebDesignFunnel = lazy(() => import("./pages/funnels/WebDesignFunnel"));
const ConsultationFunnel = lazy(() => import("./pages/funnels/ConsultationFunnel"));
const PPCFunnel = lazy(() => import("./pages/funnels/PPCFunnel"));
const LocalSEOFunnel = lazy(() => import("./pages/funnels/LocalSEOFunnel"));

 // Thank You page
 const ThankYou = lazy(() => import("./pages/ThankYou"));

// Lazy-loaded landing pages (SEO)
const Locations = lazy(() => import("./pages/Locations"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const LogoDesign = lazy(() => import("./pages/landing/LogoDesign"));
const CustomLogoDesign = lazy(() => import("./pages/landing/CustomLogoDesign"));
const MusicLogoDesign = lazy(() => import("./pages/landing/MusicLogoDesign"));
const EcommerceWebsiteDesign = lazy(() => import("./pages/landing/EcommerceWebsiteDesign"));
const ServiceWebsiteDesign = lazy(() => import("./pages/landing/ServiceWebsiteDesign"));
const CallNowLanding = lazy(() => import("./pages/landing/CallNowLanding"));
const LocalSEO = lazy(() => import("./pages/landing/LocalSEO"));
const LocalSEOChecklist = lazy(() => import("./pages/landing/LocalSEOChecklist"));
const ValentineSEO = lazy(() => import("./pages/landing/ValentineSEO"));
const NicheLandingPage = lazy(() => import("./pages/landing/NicheLandingPage"));

// Valentine SEO New Pages (Pricing, Case Studies, Comparisons)
const ValentineSeoPricing = lazy(() => import("./pages/valentine-seo/Pricing"));
const ValentineSeoCaseStudies = lazy(() => import("./pages/valentine-seo/CaseStudies"));
const ValentineSeoCaseStudyDetail = lazy(() => import("./pages/valentine-seo/CaseStudyDetail"));
const ValentineSeoComparison = lazy(() => import("./pages/valentine-seo/ComparisonPage"));
const ValentineSeoComparisonHub = lazy(() => import("./pages/valentine-seo/ComparisonHub"));
const ValentineAI = lazy(() => import("./pages/landing/valentine-seo/ValentineAI"));
const ValentineAds = lazy(() => import("./pages/landing/valentine-seo/ValentineAds"));
const ContentGenius = lazy(() => import("./pages/landing/valentine-seo/ContentGenius"));
const SiteAuditor = lazy(() => import("./pages/landing/valentine-seo/SiteAuditor"));
const SiteExplorer = lazy(() => import("./pages/landing/valentine-seo/SiteExplorer"));
const OnPageOptimizer = lazy(() => import("./pages/landing/valentine-seo/OnPageOptimizer"));
const RankTracker = lazy(() => import("./pages/landing/valentine-seo/RankTracker"));
const KeywordResearch = lazy(() => import("./pages/landing/valentine-seo/KeywordResearch"));
const TopicalMaps = lazy(() => import("./pages/landing/valentine-seo/TopicalMaps"));
const ContentOptimizer = lazy(() => import("./pages/landing/valentine-seo/ContentOptimizer"));
const BacklinkAnalyzer = lazy(() => import("./pages/landing/valentine-seo/BacklinkAnalyzer"));
const LinkBuilder = lazy(() => import("./pages/landing/valentine-seo/LinkBuilder"));
const PressReleases = lazy(() => import("./pages/landing/valentine-seo/PressReleases"));
const GBPManager = lazy(() => import("./pages/landing/valentine-seo/GBPManager"));
const GBPGalactic = lazy(() => import("./pages/landing/valentine-seo/GBPGalactic"));
const LocalCitations = lazy(() => import("./pages/landing/valentine-seo/LocalCitations"));
const LLMVisibility = lazy(() => import("./pages/landing/valentine-seo/LLMVisibility"));
const ReportBuilder = lazy(() => import("./pages/landing/valentine-seo/ReportBuilder"));
const Implementation = lazy(() => import("./pages/landing/valentine-seo/Implementation"));
const HelpCenter = lazy(() => import("./pages/landing/valentine-seo/HelpCenter"));

// Valentine SEO Funnels
const ValentineAIFunnel = lazy(() => import("./pages/funnels/valentine-seo/ValentineAIFunnel"));
const ValentineAdsFunnel = lazy(() => import("./pages/funnels/valentine-seo/ValentineAdsFunnel"));
const ContentGeniusFunnel = lazy(() => import("./pages/funnels/valentine-seo/ContentGeniusFunnel"));
const SiteAuditorFunnel = lazy(() => import("./pages/funnels/valentine-seo/SiteAuditorFunnel"));
const SiteExplorerFunnel = lazy(() => import("./pages/funnels/valentine-seo/SiteExplorerFunnel"));
const OnPageOptimizerFunnel = lazy(() => import("./pages/funnels/valentine-seo/OnPageOptimizerFunnel"));
const RankTrackerFunnel = lazy(() => import("./pages/funnels/valentine-seo/RankTrackerFunnel"));
const KeywordResearchFunnel = lazy(() => import("./pages/funnels/valentine-seo/KeywordResearchFunnel"));
const TopicalMapsFunnel = lazy(() => import("./pages/funnels/valentine-seo/TopicalMapsFunnel"));
const ContentOptimizerFunnel = lazy(() => import("./pages/funnels/valentine-seo/ContentOptimizerFunnel"));
const BacklinkAnalyzerFunnel = lazy(() => import("./pages/funnels/valentine-seo/BacklinkAnalyzerFunnel"));
const LinkBuilderFunnel = lazy(() => import("./pages/funnels/valentine-seo/LinkBuilderFunnel"));
const PressReleasesFunnel = lazy(() => import("./pages/funnels/valentine-seo/PressReleasesFunnel"));
const GBPManagerFunnel = lazy(() => import("./pages/funnels/valentine-seo/GBPManagerFunnel"));
const GBPGalacticFunnel = lazy(() => import("./pages/funnels/valentine-seo/GBPGalacticFunnel"));
const LocalCitationsFunnel = lazy(() => import("./pages/funnels/valentine-seo/LocalCitationsFunnel"));
const LLMVisibilityFunnel = lazy(() => import("./pages/funnels/valentine-seo/LLMVisibilityFunnel"));
const ReportBuilderFunnel = lazy(() => import("./pages/funnels/valentine-seo/ReportBuilderFunnel"));
const ImplementationFunnel = lazy(() => import("./pages/funnels/valentine-seo/ImplementationFunnel"));
const CovaRoofingAudit = lazy(() => import("./pages/CovaRoofingAudit"));

// Lazy-loaded location pages (SEO)
const CountyPage = lazy(() => import("./pages/location/CountyPage"));
const CityPage = lazy(() => import("./pages/location/CityPage"));
const ServiceCityPage = lazy(() => import("./pages/location/ServiceCityPage"));

// Lazy-loaded portal pages
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const ResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const Reports = lazy(() => import("./pages/portal/Reports"));
const Invoices = lazy(() => import("./pages/portal/Invoices"));
const Projects = lazy(() => import("./pages/portal/Projects"));
const PortalFiles = lazy(() => import("./pages/portal/PortalFiles"));
const InvoiceDetail = lazy(() => import("./pages/portal/InvoiceDetail"));
const ProjectDetail = lazy(() => import("./pages/portal/ProjectDetail"));
const Support = lazy(() => import("./pages/portal/Support"));
const Settings = lazy(() => import("./pages/portal/Settings"));
const Integrations = lazy(() => import("./pages/portal/Integrations"));
const UserManagement = lazy(() => import("./pages/portal/admin/UserManagement"));
const OrgManagement = lazy(() => import("./pages/portal/admin/OrgManagement"));
const AdminSettings = lazy(() => import("./pages/portal/admin/AdminSettings"));
const FunnelSettings = lazy(() => import("./pages/portal/admin/FunnelSettings"));
const BookingSettings = lazy(() => import("./pages/portal/admin/BookingSettings"));
const BookingManagement = lazy(() => import("./pages/portal/admin/BookingManagement"));
const Provisions = lazy(() => import("./pages/portal/admin/Provisions"));
const IntakeSubmissions = lazy(() => import("./pages/portal/admin/IntakeSubmissions"));
const OrgModules = lazy(() => import("./pages/portal/admin/OrgModules"));
const ReportEntry = lazy(() => import("./pages/portal/admin/ReportEntry"));
const EmbeddedReportEntry = lazy(() => import("./pages/portal/admin/EmbeddedReportEntry"));
const InvoiceEntry = lazy(() => import("./pages/portal/admin/InvoiceEntry"));
const ProjectEntry = lazy(() => import("./pages/portal/admin/ProjectEntry"));
const AuthorityProjectsAdmin = lazy(() => import("./pages/portal/admin/AuthorityProjects"));
const ProjectsIndex = lazy(() => import("./pages/ProjectsIndex"));
const Industries = lazy(() => import("./pages/Industries"));
const IndustryDetail = lazy(() => import("./pages/IndustryDetail"));
const ProjectDetailPublic = lazy(() => import("./pages/ProjectDetailPublic"));
const FunnelAnalytics = lazy(() => import("./pages/portal/FunnelAnalytics"));
const AcceptInvitation = lazy(() => import("./pages/portal/AcceptInvitation"));
const PaymentSuccess = lazy(() => import("./pages/portal/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/portal/PaymentCanceled"));
// Lazy-loaded CRM pages
const CRMDashboard = lazy(() => import("./pages/portal/crm/CRMDashboard"));
const Contacts = lazy(() => import("./pages/portal/crm/Contacts"));
const ContactNew = lazy(() => import("./pages/portal/crm/ContactNew"));
const ContactDetail = lazy(() => import("./pages/portal/crm/ContactDetail"));
const Companies = lazy(() => import("./pages/portal/crm/Companies"));
const CompanyNew = lazy(() => import("./pages/portal/crm/CompanyNew"));
const CompanyDetail = lazy(() => import("./pages/portal/crm/CompanyDetail"));
const Pipeline = lazy(() => import("./pages/portal/crm/Pipeline"));
const DealNew = lazy(() => import("./pages/portal/crm/DealNew"));
const DealDetail = lazy(() => import("./pages/portal/crm/DealDetail"));
const Tasks = lazy(() => import("./pages/portal/crm/Tasks"));
const Analytics = lazy(() => import("./pages/portal/crm/Analytics"));

// Lazy-loaded campaign pages
const Campaigns = lazy(() => import("./pages/portal/Campaigns"));
const CampaignNew = lazy(() => import("./pages/portal/CampaignNew"));
const CampaignDetail = lazy(() => import("./pages/portal/CampaignDetail"));
const EmailTemplates = lazy(() => import("./pages/portal/EmailTemplates"));
const Sequences = lazy(() => import("./pages/portal/Sequences"));
const SequenceDetail = lazy(() => import("./pages/portal/SequenceDetail"));

// Lazy-loaded social pages
const SocialMedia = lazy(() => import("./pages/portal/SocialMedia"));
const MediaLibrary = lazy(() => import("./pages/portal/MediaLibrary"));
const KnowledgeBase = lazy(() => import("./pages/portal/KnowledgeBase"));
const MasterCalendar = lazy(() => import("./pages/portal/MasterCalendar"));
const ABTesting = lazy(() => import("./pages/portal/ABTesting"));

// Lazy-loaded micro-SaaS app pages
const ReviewsBooster = lazy(() => import("./pages/portal/apps/ReviewsBooster"));
const ReviewsCustomers = lazy(() => import("./pages/portal/apps/reviews/ReviewsCustomers"));
const ReviewsCampaigns = lazy(() => import("./pages/portal/apps/reviews/ReviewsCampaigns"));
const ReviewsFeedback = lazy(() => import("./pages/portal/apps/reviews/ReviewsFeedback"));
const BeforeAfterProof = lazy(() => import("./pages/portal/apps/BeforeAfterProof"));
const JobPricing = lazy(() => import("./pages/portal/apps/JobPricing"));
const QuoteBuilder = lazy(() => import("./pages/portal/apps/QuoteBuilder"));
const AdSpendOptimizer = lazy(() => import("./pages/portal/apps/AdSpendOptimizer"));
const LandingPageTester = lazy(() => import("./pages/portal/apps/LandingPageTester"));
const GeoGridDashboard = lazy(() => import("./pages/portal/apps/GeoGridDashboard"));
const GBPScheduler = lazy(() => import("./pages/portal/apps/GBPScheduler"));
const DominanceTracker = lazy(() => import("./pages/portal/apps/DominanceTracker"));

// Super Admin pages
const LiveWebsites = lazy(() => import("./pages/LiveWebsites"));
const LiveWebsiteDetail = lazy(() => import("./pages/LiveWebsiteDetail"));
const LiveWebsiteCaseStudy = lazy(() => import("./pages/LiveWebsiteCaseStudy"));
const ProofCommandCenter = lazy(() => import("./pages/ProofCommandCenter"));
const SuperAdminDashboard = lazy(() => import("./pages/portal/superadmin/SuperAdminDashboard"));
const AllCompanies = lazy(() => import("./pages/portal/superadmin/AllCompanies"));
const AllOrganizations = lazy(() => import("./pages/portal/superadmin/AllOrganizations"));
const AllUsers = lazy(() => import("./pages/portal/superadmin/AllUsers"));
import { PortalLayout } from "./components/portal/PortalLayout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <PaymentTestModeBanner />
            <RedirectHandler>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/checkout/return" element={<CheckoutReturn />} />
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/about/dallas" element={<AuthorProfile />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/solutions/:id" element={<SolutionDetail />} />
                  <Route path="/services/:id" element={<ServiceDetail />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/portfolio/:id" element={<CaseStudy />} />
                  <Route path="/case-studies" element={<AgencyCaseStudies />} />
                  <Route path="/case-studies/:slug" element={<AgencyCaseStudyDetail />} />
                  <Route path="/live-websites" element={<LiveWebsites />} />
                  <Route path="/live-websites/:id" element={<LiveWebsiteDetail />} />
                  <Route path="/live-websites/:id/case-study" element={<LiveWebsiteCaseStudy />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/offer" element={<Offer />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/category/:categorySlug" element={<Blog />} />
                  <Route path="/blog/categories" element={<BlogCategories />} />
                  <Route path="/blog/tag/:tagSlug" element={<BlogTag />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/hosting" element={<Hosting />} />
                  <Route path="/projects" element={<ProjectsIndex />} />
                  <Route path="/projects/:slug" element={<ProjectDetailPublic />} />
                  <Route path="/industries" element={<Industries />} />
                  <Route path="/industries/:slug" element={<IndustryDetail />} />

                  {/* Court Services Showcase */}
                  <Route path="/showcase/court-services" element={<CourtHome />} />
                  <Route path="/showcase/court-services/services" element={<CourtServicesPage />} />
                  <Route path="/showcase/court-services/jurisdictions" element={<CourtJurisdictions />} />
                  <Route path="/showcase/court-services/faq" element={<CourtFAQ />} />
                  <Route path="/showcase/court-services/contact" element={<CourtContact />} />
                  <Route path="/showcase/court-services/about" element={<CourtAbout />} />
                  
                  {/* Public Quote Viewing */}
                  <Route path="/quote" element={<PublicQuote />} />
                  
                  {/* Client Intake Form (hidden link) */}
                  <Route path="/start" element={<ClientIntake />} />
                  
                  {/* Public Project Brief (shareable) */}
                  <Route path="/project-brief" element={<ClientIntake />} />
                  
                  {/* ARCHITECT Growth Intelligence Intake */}
                  <Route path="/architect" element={<Architect />} />
                  <Route path="/architect-v2" element={<ArchitectV2 />} />
                  
                  {/* Funnel Routes */}
                  <Route path="/funnel/seo-audit" element={<SEOAuditFunnel />} />
                  <Route path="/funnel/web-design" element={<WebDesignFunnel />} />
                  <Route path="/funnel/consultation" element={<ConsultationFunnel />} />
                  <Route path="/funnel/ppc" element={<PPCFunnel />} />
                  <Route path="/funnel/local-seo" element={<LocalSEOFunnel />} />
                  
                  {/* Thank You Page */}
                  <Route path="/thank-you" element={<ThankYou />} />
                  
                  {/* SEO Landing Pages */}
                  <Route path="/locations" element={<Locations />} />
                  <Route path="/sitemap" element={<Sitemap />} />
                  <Route path="/logo-design" element={<LogoDesign />} />
                  <Route path="/custom-logo-design" element={<CustomLogoDesign />} />
                  <Route path="/music-logo-design" element={<MusicLogoDesign />} />
                  <Route path="/ecommerce-website-design" element={<EcommerceWebsiteDesign />} />
                  <Route path="/service-website-design" element={<ServiceWebsiteDesign />} />
                  <Route path="/call" element={<CallNowLanding />} />
                  <Route path="/local-seo" element={<LocalSEO />} />
                  <Route path="/local-seo-audit-checklist" element={<LocalSEOChecklist />} />
                  <Route path="/valentine-seo" element={<ValentineSEO />} />
                  <Route path="/valentine-seo/valentine-ai" element={<ValentineAI />} />
                  <Route path="/valentine-seo/valentine-ads" element={<ValentineAds />} />
                  <Route path="/valentine-seo/content-genius" element={<ContentGenius />} />
                  <Route path="/valentine-seo/site-auditor" element={<SiteAuditor />} />
                  <Route path="/valentine-seo/site-explorer" element={<SiteExplorer />} />
                  <Route path="/valentine-seo/on-page-optimizer" element={<OnPageOptimizer />} />
                  <Route path="/valentine-seo/rank-tracker" element={<RankTracker />} />
                  <Route path="/valentine-seo/keyword-research" element={<KeywordResearch />} />
                  <Route path="/valentine-seo/topical-maps" element={<TopicalMaps />} />
                  <Route path="/valentine-seo/content-optimizer" element={<ContentOptimizer />} />
                  <Route path="/valentine-seo/backlink-analyzer" element={<BacklinkAnalyzer />} />
                  <Route path="/valentine-seo/link-builder" element={<LinkBuilder />} />
                  <Route path="/valentine-seo/press-releases" element={<PressReleases />} />
                  <Route path="/valentine-seo/gbp-manager" element={<GBPManager />} />
                  <Route path="/valentine-seo/gbp-galactic" element={<GBPGalactic />} />
                  <Route path="/valentine-seo/local-citations" element={<LocalCitations />} />
                  <Route path="/valentine-seo/llm-visibility" element={<LLMVisibility />} />
                  <Route path="/valentine-seo/reports" element={<ReportBuilder />} />
                  <Route path="/valentine-seo/implementation" element={<Implementation />} />
                  <Route path="/valentine-seo/help" element={<HelpCenter />} />
                  <Route path="/valentine-seo/pricing" element={<ValentineSeoPricing />} />
                  <Route path="/valentine-seo/case-studies" element={<ValentineSeoCaseStudies />} />
                  <Route path="/valentine-seo/case-studies/:slug" element={<ValentineSeoCaseStudyDetail />} />
                  <Route path="/valentine-seo/compare" element={<ValentineSeoComparisonHub />} />
                  <Route path="/valentine-seo/vs/:competitor" element={<ValentineSeoComparison />} />
                  {/* Valentine SEO Funnels */}
                  <Route path="/funnel/valentine-ai" element={<ValentineAIFunnel />} />
                  <Route path="/funnel/valentine-ads" element={<ValentineAdsFunnel />} />
                  <Route path="/funnel/content-genius" element={<ContentGeniusFunnel />} />
                  <Route path="/funnel/site-auditor" element={<SiteAuditorFunnel />} />
                  <Route path="/funnel/site-explorer" element={<SiteExplorerFunnel />} />
                  <Route path="/funnel/on-page-optimizer" element={<OnPageOptimizerFunnel />} />
                  <Route path="/funnel/rank-tracker" element={<RankTrackerFunnel />} />
                  <Route path="/funnel/keyword-research" element={<KeywordResearchFunnel />} />
                  <Route path="/funnel/topical-maps" element={<TopicalMapsFunnel />} />
                  <Route path="/funnel/content-optimizer" element={<ContentOptimizerFunnel />} />
                  <Route path="/funnel/backlink-analyzer" element={<BacklinkAnalyzerFunnel />} />
                  <Route path="/funnel/link-builder" element={<LinkBuilderFunnel />} />
                  <Route path="/funnel/press-releases" element={<PressReleasesFunnel />} />
                  <Route path="/funnel/gbp-manager" element={<GBPManagerFunnel />} />
                  <Route path="/funnel/gbp-galactic" element={<GBPGalacticFunnel />} />
                  <Route path="/funnel/local-citations" element={<LocalCitationsFunnel />} />
                  <Route path="/funnel/llm-visibility" element={<LLMVisibilityFunnel />} />
                  <Route path="/funnel/reports" element={<ReportBuilderFunnel />} />
                  <Route path="/funnel/implementation" element={<ImplementationFunnel />} />
                  <Route path="/audits/cova-roofing" element={<CovaRoofingAudit />} />
                  <Route path="/cova-roofing-audit" element={<CovaRoofingAudit />} />

                  {/* Explicit destination for invalid dynamic slugs. Without this
                      route, /not-found is captured by /:nicheSlug and redirects
                      back to itself, leaving visitors on a blank screen. */}
                  <Route path="/not-found" element={<NotFound />} />
                  
                  {/* Location-based SEO Pages (hierarchical) */}
                  <Route path="/:countySlug/:citySlug/:serviceSlug" element={<ServiceCityPage />} />
                  <Route path="/:countySlug/:citySlug" element={<CityPage />} />
                  <Route path="/huron-county" element={<CountyPage />} />
                  <Route path="/tuscola-county" element={<CountyPage />} />
                  <Route path="/sanilac-county" element={<CountyPage />} />
                  
                  {/* Niche Industry Landing Pages */}
                  <Route path="/:nicheSlug" element={<NicheLandingPage />} />
                  
                  {/* Portal Routes */}
                  <Route path="/portal/login" element={<PortalLogin />} />
                  <Route path="/portal/reset-password" element={<ResetPassword />} />
                  <Route path="/portal/accept-invite" element={<AcceptInvitation />} />
                  <Route path="/portal/payment-success" element={<PaymentSuccess />} />
                  <Route path="/portal/payment-canceled" element={<PaymentCanceled />} />
                  <Route path="/portal" element={<PortalLayout />}>
                    <Route index element={<PortalDashboard />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="invoices" element={<Invoices />} />
                    <Route path="invoices/:id" element={<InvoiceDetail />} />
                    <Route path="projects" element={<Projects />} />
                    <Route path="projects/:id" element={<ProjectDetail />} />
                    <Route path="files" element={<PortalFiles />} />
                    <Route path="support" element={<Support />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="settings/integrations" element={<Integrations />} />
                    <Route path="admin/settings" element={<AdminSettings />} />
                    <Route path="admin/users" element={<UserManagement />} />
                    <Route path="admin/organizations" element={<OrgManagement />} />
                    <Route path="admin/provisions" element={<Provisions />} />
                    <Route path="admin/modules" element={<OrgModules />} />
                    <Route path="admin/reports/new" element={<ReportEntry />} />
                    <Route path="admin/reports/embed" element={<EmbeddedReportEntry />} />
                    <Route path="admin/invoices/new" element={<InvoiceEntry />} />
                    <Route path="admin/projects/new" element={<ProjectEntry />} />
                    <Route path="admin/authority" element={<AuthorityProjectsAdmin />} />
                    <Route path="admin/funnels" element={<FunnelAnalytics />} />
                    <Route path="admin/funnel-settings" element={<FunnelSettings />} />
                    <Route path="admin/booking-settings" element={<BookingSettings />} />
                    <Route path="admin/bookings" element={<BookingManagement />} />
                    <Route path="admin/intake-submissions" element={<IntakeSubmissions />} />
                    <Route path="crm" element={<CRMDashboard />} />
                    <Route path="crm/contacts" element={<Contacts />} />
                    <Route path="crm/contacts/new" element={<ContactNew />} />
                    <Route path="crm/contacts/:id" element={<ContactDetail />} />
                    <Route path="crm/companies" element={<Companies />} />
                    <Route path="crm/companies/new" element={<CompanyNew />} />
                    <Route path="crm/companies/:id" element={<CompanyDetail />} />
                    <Route path="crm/pipeline" element={<Pipeline />} />
                    <Route path="crm/tasks" element={<Tasks />} />
                    <Route path="crm/analytics" element={<Analytics />} />
                    <Route path="crm/deals/new" element={<DealNew />} />
                    <Route path="crm/deals/:id" element={<DealDetail />} />
                    <Route path="campaigns" element={<Campaigns />} />
                    <Route path="campaigns/new" element={<CampaignNew />} />
                    <Route path="campaigns/:id" element={<CampaignDetail />} />
                    <Route path="campaigns/templates" element={<EmailTemplates />} />
                    <Route path="campaigns/sequences" element={<Sequences />} />
                    <Route path="campaigns/sequences/:id" element={<SequenceDetail />} />
                    <Route path="social" element={<SocialMedia />} />
                    <Route path="social/media" element={<MediaLibrary />} />
                    <Route path="social/knowledge" element={<KnowledgeBase />} />
                    <Route path="calendar" element={<MasterCalendar />} />
                    <Route path="admin/ab-testing" element={<ABTesting />} />
                    {/* Micro-SaaS Apps */}
                    <Route path="apps/reviews" element={<ReviewsBooster />} />
                    <Route path="apps/reviews/customers" element={<ReviewsCustomers />} />
                    <Route path="apps/reviews/campaigns" element={<ReviewsCampaigns />} />
                    <Route path="apps/reviews/feedback" element={<ReviewsFeedback />} />
                    <Route path="apps/proof" element={<BeforeAfterProof />} />
                    <Route path="apps/pricing" element={<JobPricing />} />
                    <Route path="apps/pricing/new" element={<QuoteBuilder />} />
                    <Route path="apps/ads" element={<AdSpendOptimizer />} />
                    <Route path="apps/ab-test" element={<LandingPageTester />} />
                    <Route path="apps/geo-grid" element={<GeoGridDashboard />} />
                    <Route path="apps/gbp-scheduler" element={<GBPScheduler />} />
                    <Route path="apps/dominance-tracker" element={<DominanceTracker />} />
                    <Route path="admin/proof-command-center" element={<ProofCommandCenter />} />
                    {/* Super Admin Routes */}
                    <Route path="super-admin" element={<SuperAdminDashboard />} />
                    <Route path="super-admin/proof-command-center" element={<ProofCommandCenter />} />
                    <Route path="super-admin/companies" element={<AllCompanies />} />
                    <Route path="super-admin/organizations" element={<AllOrganizations />} />
                    <Route path="super-admin/users" element={<AllUsers />} />

                  </Route>
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </RedirectHandler>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
